/**
 * @file src/pages/company-onboarding-wizard/components/StepRouter.jsx
 * @description Renders the correct step component or join form based on
 * activeTab and currentStep. Also manages the skip option display.
 *
 * Responsibilities:
 *  - Route between create wizard steps and join form
 *  - Render skip option outside AnimatePresence to prevent unmount flicker
 *
 * Rules:
 *  - No state owned here — all via props
 *  - No API calls, no validation
 *  - Pure presentational router
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TABS } from "../constants";
import Step1CompanyInfo from "./Step1CompanyInfo";
import Step2Address from "./Step2Address";
import Step3ContactInfo from "./Step3ContactInfo";
import JoinCompanyForm from "./JoinCompanyForm";
import SkipCompanySetup from "./SkipCompanySetup";

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const tabVariants = {
  initial: (direction) => ({
    opacity: 0,
    x: direction === TABS.CREATE ? 20 : -20,
  }),
  animate: { opacity: 1, x: 0 },
  exit: (direction) => ({
    opacity: 0,
    x: direction === TABS.CREATE ? -20 : 20,
  }),
};

const stepVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

// ---------------------------------------------------------------------------
// StepRouter
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   activeTab:     string,
 *   currentStep:   number,
 *   formData:      object,
 *   errors:        object,
 *   updateFormData: (field: string, value: any) => void,
 *   onNext:        () => void,
 *   onPrevious:    () => void,
 *   onJoin:        (inviteCode: string) => Promise<void>,
 *   onSkip:        () => Promise<void>,
 *   isJoining:     boolean,
 * }} props
 */
const StepRouter = ({
  activeTab,
  currentStep,
  formData,
  errors,
  updateFormData,
  onNext,
  onPrevious,
  onJoin,
  onSkip,
  isJoining,
}) => {
  const [showSkipOption, setShowSkipOption] = useState(false);

  const stepProps = {
    formData,
    errors,
    updateFormData,
    onNext,
    onPrevious,
  };

  return (
    <AnimatePresence mode="wait" custom={activeTab}>
      {activeTab === TABS.CREATE ? (
        <motion.div
          key="create"
          custom={activeTab}
          variants={tabVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.25 }}
        >
          {/* ── Step content ──────────────────────────────────────────────── */}
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step-1"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
              >
                <Step1CompanyInfo {...stepProps} />
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step-2"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
              >
                <Step2Address {...stepProps} />
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step-3"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
              >
                <Step3ContactInfo {...stepProps} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Skip option ───────────────────────────────────────────────────
              Rendered OUTSIDE the inner AnimatePresence so it does not get
              unmounted when steps transition — prevents the flicker that
              occurred in the original when showSkipOption was inside the
              step AnimatePresence.                                          */}
          {!showSkipOption ? (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowSkipOption(true)}
                className="text-sm text-gray-500 hover:text-primary transition-colors"
              >
                Skip for now →
              </button>
            </div>
          ) : (
            <SkipCompanySetup
              onSkip={onSkip}
              onCancel={() => setShowSkipOption(false)}
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key="join"
          custom={activeTab}
          variants={tabVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.25 }}
        >
          <JoinCompanyForm onJoin={onJoin} isLoading={isJoining} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StepRouter;
